// src/components/sales/client-interactions/ClientInteractionModal.jsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { XCircle, CheckCircle } from "lucide-react";
import ModalPortal from "../../../shared/ui/ModalPortal"; // عدّلي المسار حسب مكانه عندك

const empty = {
  clientId: "",
  interactionType: "",
  clientRequest: "",
  report: "",
  interactionDate: "", // optional (datetime-local)
};

const norm = (v) => String(v ?? "").trim();

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export default function ClientInteractionModal({
  initialData,
  clients,
  loadingClients,
  onClose,
  onSubmit,
}) {
  const { t, i18n } = useTranslation();
  void i18n;
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(() => ({
    ...empty,
    ...(initialData || {}),
    clientId:
      initialData?.clientId !== undefined && initialData?.clientId !== null
        ? String(initialData.clientId)
        : "",
    interactionDate: initialData?.interactionDate
      ? toLocalInputValue(initialData.interactionDate)
      : "",
    report: initialData?.report ?? "",
    clientRequest: initialData?.clientRequest ?? "",
    interactionType: initialData?.interactionType ?? "",
  }));

  const title = useMemo(
    () =>
      initialData
        ? t("sales.clientInteractions.modal.editTitle")
        : t("sales.clientInteractions.modal.addTitle"),
    [initialData, t]
  );

  const change = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!norm(form.clientId)) return alert(t("sales.clientInteractions.validation.clientRequired"));
    if (!norm(form.interactionType))
      return alert(t("sales.clientInteractions.validation.typeRequired"));

    // ✅ Swagger: clientRequest REQUIRED
    if (!norm(form.clientRequest))
      return alert(t("sales.clientInteractions.validation.requestRequired"));

    // ✅ Swagger: interactionDate OPTIONAL (nullable)
    let interactionDate = null;
    if (norm(form.interactionDate)) {
      const d = new Date(form.interactionDate);
      if (Number.isNaN(d.getTime()))
        return alert(t("sales.clientInteractions.validation.invalidDate"));
      interactionDate = d.toISOString();
    }

    const payload = {
      clientId: Number(form.clientId),
      interactionType: norm(form.interactionType),
      clientRequest: norm(form.clientRequest),
      // report nullable => send null if empty
      report: norm(form.report) ? norm(form.report) : null,
      // interactionDate nullable
      interactionDate,
    };

    setSaving(true);
    try {
      await onSubmit?.(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal onClose={saving ? undefined : onClose}>
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onMouseDown={saving ? undefined : onClose}
      />

      {/* Center */}
      <div className="absolute inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <div
          role="dialog"
          aria-modal="true"
          className="w-full max-w-4xl bg-white/10 backdrop-blur-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.3)] rounded-2xl overflow-hidden my-10"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-700 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-white">{title}</h2>
            <button
              onClick={saving ? undefined : onClose}
              className="text-white hover:text-gray-200 disabled:opacity-50"
              disabled={saving}
              title={t("common.close")}
              type="button"
            >
              <XCircle className="w-7 h-7" />
            </button>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto text-white">
            <div className="grid md:grid-cols-2 gap-4">
              <Box label={t("sales.clientInteractions.modal.clientLabel")}>
                <select
                  value={form.clientId}
                  onChange={change("clientId")}
                  className={selectCls}
                  disabled={loadingClients || saving}
                >
                  <option value="" className="text-black">
                    {loadingClients
                      ? t("common.loading")
                      : t("sales.clientInteractions.modal.selectClient")}
                  </option>
                  {(clients || []).map((c) => (
                    <option key={c.id} value={String(c.id)} className="text-black">
                      {c.companyName || t("sales.clientInteractions.clientFallback")} — #{c.id}
                    </option>
                  ))}
                </select>
              </Box>

              <Box label={t("sales.clientInteractions.modal.typeLabel")}>
                <input
                  value={form.interactionType}
                  onChange={change("interactionType")}
                  className={inputCls}
                  disabled={saving}
                  placeholder={t("sales.clientInteractions.modal.typePlaceholder")}
                />
              </Box>

              <Box label={t("sales.clientInteractions.modal.dateLabel")}>
                <input
                  type="datetime-local"
                  value={form.interactionDate}
                  onChange={change("interactionDate")}
                  className={inputCls}
                  disabled={saving}
                />
              </Box>

              <Box label={t("sales.clientInteractions.modal.requestLabel")}>
                <textarea
                  rows={4}
                  value={form.clientRequest}
                  onChange={change("clientRequest")}
                  className={textareaCls}
                  disabled={saving}
                  placeholder={t("sales.clientInteractions.modal.requestPlaceholder")}
                />
              </Box>

              <Box label={t("sales.clientInteractions.modal.reportLabel")}>
                <textarea
                  rows={4}
                  value={form.report}
                  onChange={change("report")}
                  className={textareaCls}
                  disabled={saving}
                  placeholder={t("sales.clientInteractions.modal.reportPlaceholder")}
                />
              </Box>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={submit}
                disabled={saving}
                className="flex-1 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-red-500 shadow-lg hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
                type="button"
              >
                <CheckCircle className="w-5 h-5" />
                {saving ? t("sales.clientInteractions.saving") : t("sales.clientInteractions.save")}
              </button>

              <button
                onClick={saving ? undefined : onClose}
                className="flex-1 py-3 rounded-xl font-semibold bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-40"
                disabled={saving}
                type="button"
              >
                {t("common.cancel")}
              </button>
            </div>

            <p className="mt-4 text-xs text-white/60">
              {t("sales.clientInteractions.modal.note")}
            </p>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function Box({ label, children }) {
  return (
    <div className="bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-xl">
      <label className="text-sm text-gray-200">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30";
const textareaCls =
  "w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30 resize-none";
const selectCls =
  "w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30";
